import React from 'react';
import { motion } from 'framer-motion';

const examples = [
  {
    title: 'Live Chat on Restaurant Website',
    description: 'Seamlessly integrated into your existing website',
    image: '/examples/website-integration.png',
    tags: ['Desktop', 'Mobile Responsive'],
  },
  {
    title: 'Customer Enquiry Flow',
    description: 'Handling menu, hours, and reservation queries',
    image: '/examples/enquiry-flow.png',
    tags: ['Menu Info', 'Booking', 'Directions'],
  },
  {
    title: 'Multilingual Conversations',
    description: 'Automatic language detection and response',
    image: '/examples/multilingual.png',
    tags: ['English', 'Spanish', 'Chinese', '+97 more'],
  },
  {
    title: 'White-labeled Interface',
    description: 'Your brand, your colors, your voice',
    image: '/examples/white-label.png',
    tags: ['Custom Logo', 'Brand Colors', 'Unique Tone'],
  },
];

const WorkingExamples: React.FC = () => {
  return (
    <section id="examples" className="py-20 bg-white">
      <div className="max-width-container">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            📸 Kairos In Action
          </h2>
          <p className="text-xl text-slate-700 max-w-3xl mx-auto">
            See how Kairos transforms regular websites into interactive experiences.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {examples.map((example, index) => (
            <motion.div
              key={index}
              className="group cursor-pointer"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -10 }}
            >
              <div className="bg-gray-50 rounded-2xl overflow-hidden shadow-lg group-hover:shadow-2xl transition-all duration-300">
                {/* Placeholder for actual screenshots */}
                <div className="relative h-64 bg-gradient-to-br from-blue-100 to-emerald-100 flex items-center justify-center">
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-br from-blue-200 to-emerald-200 opacity-0 group-hover:opacity-30 transition-opacity duration-300"
                  />
                  <div className="text-center">
                    <div className="text-6xl mb-4">🖼️</div>
                    <p className="text-slate-600 text-sm">Screenshot Preview</p>
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">
                    {example.title}
                  </h3>
                  <p className="text-slate-600 mb-4">
                    {example.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {example.tags.map((tag, tagIndex) => (
                      <motion.span
                        key={tagIndex}
                        className="px-3 py-1 bg-blue-100 text-blue-700 text-xs rounded-full"
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.3, delay: index * 0.1 + tagIndex * 0.05 }}
                      >
                        {tag}
                      </motion.span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div 
          className="bg-gradient-to-r from-blue-50 to-emerald-50 rounded-2xl p-8 text-center"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <p className="text-lg text-slate-800">
            <span className="font-semibold">Note:</span> These are just examples — every Kairos instance is 
            <span className="text-blue-600 font-semibold"> custom-built and white-labeled</span> for you.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default WorkingExamples;